/**
 * ProgressContext.tsx
 * -------------------
 * Provides global progress state for the onboarding flow.
 * - Tracks current step, test pass status, and language.
 * - Persists state to localStorage.
 * - Exposes context and hook for use in components.
 */


import React, { createContext, useContext, useState, useEffect } from 'react';

interface ProgressContextType {
  step: number;
  setStep: (step: number) => void;
  testPassed: boolean;
  setTestPassed: (passed: boolean) => void;
  language: string;
  setLanguage: (lang: string) => void;
}

const ProgressContext = createContext<ProgressContextType | undefined>(undefined);

export const ProgressProvider: React.FC<React.PropsWithChildren<object>> = ({ children }) => {
  const [step, setStep] = useState<number>(() => Number(localStorage.getItem('step')) || 0);
  const [testPassed, setTestPassed] = useState<boolean>(
    () => localStorage.getItem('testPassed') === 'true',
  );
  const [language, setLanguage] = useState<string>(() => localStorage.getItem('language') ?? 'en');

  useEffect(() => {
    localStorage.setItem('step', String(step));
  }, [step]);
  useEffect(() => {
    localStorage.setItem('testPassed', String(testPassed));
  }, [testPassed]);
  useEffect(() => {
    localStorage.setItem('language', language);
  }, [language]);

  const contextValue = React.useMemo(
    () => ({ step, setStep, testPassed, setTestPassed, language, setLanguage }),
    [step, setStep, testPassed, setTestPassed, language, setLanguage],
  );

  return <ProgressContext.Provider value={contextValue}>{children}</ProgressContext.Provider>;
};

export const useProgress = () => {
  const context = useContext(ProgressContext);
  if (!context) throw new Error('useProgress must be used within ProgressProvider');
  return context;
};
